define('core/views/ContentEditableView',[
    'jquery',
    'underscore',
    'core/editable',
    'core/views/TextareaView',
], function (
    $,
    _,
    Editable,
    TextareaView
) {
    'use strict';

    var doc = window.document;

    var parent = TextareaView;
    var parentProto = parent.prototype;

    var ContentEditableView = parent.extend({
        events: _.defaults({
            'focusout [data-role=editable]': 'handleFocusOut',
            'click .placeholder': 'handlePlaceholderClick',
        }, parentProto.events),

        initialize: function () {
            parentProto.initialize.apply(this, arguments);

            this.hasFocus = false;
            this._selectionRange = null;
        },

        saveSelection: function () {
            var sel = window.getSelection();
            this._selectionRange = sel && sel.rangeCount && sel.getRangeAt(0);
        },

        restoreSelection: function () {
            if (!this._selectionRange)
                return;

            var sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(this._selectionRange);

            this._selectionRange = null;
        },

        render: function () {
            this.$input = this.createInput();
            this.$el.append(this.$input);
            this.set(this.value);
            this.renderPlaceholder();
            return this;
        },

        createInput: function () {
            var $input = $('<div>').attr({
                'class': 'textarea',
                'tabIndex': 0,
                'role': 'textbox',
                'aria-multiline': 'true',
                'contenteditable': 'PLAINTEXT-ONLY',
                'data-role': 'editable',
            }).css({
                'overflow': 'auto',
                'word-wrap': 'break-word',
                // MAX_TEXTAREA_HEIGHT inherited from parent constructor (TextareaView)
                'max-height': this.constructor.MAX_TEXTAREA_HEIGHT + 'px',
            });

            var input = $input[0];

            // NOTE: See http://stackoverflow.com/a/18316972/90297 for the 'plaintext-only' thing
            if (input.contentEditable !== 'plaintext-only')
                input.contentEditable = 'true';

            this.content = new Editable(input, true);

            return $input;
        },

        renderPlaceholder: function () {
            // contentEditable doesn't work w/ HTML5 input placeholders
            var placeholder = this.placeholder;
            if (!placeholder)
                return;

            // NOTE: Placeholder is **not** HTML escaped!

            // Set the placeholder as aria-label to make it accessible to
            // screen readers. They won't recognize a "placeholder"
            // attribute on a span or div.
            this.$input.attr('aria-label', placeholder);
            this.$placeholder = $('<span class="placeholder">' + placeholder + '</span>');
            this.updatePlaceholderDisplay();
        },

        // Show or hide the placeholder, dependent on
        // state of textarea focus and content.
        updatePlaceholderDisplay: function () {
            if (!this.$placeholder)
                return;

            if (!this.hasFocus && !this.content.text())
                this.$el.prepend(this.$placeholder);
            else
                this.$placeholder.remove();
        },

        handlePlaceholderClick: function () {
            // Note that clicking on placeholder doesn't focus inside
            // actual textarea body; we need to do that ourselves

            // NOTE: This will trigger the handleFocusIn event below
            this.$input.focus();
        },

        handleFocusIn: function () {
            parentProto.handleFocusIn.call(this);

            this.restoreSelection();

            this.hasFocus = true;
            this.updatePlaceholderDisplay();
        },

        handleFocusOut: function () {
            this.saveSelection();
            this.hasFocus = false;
            this.updatePlaceholderDisplay();
        },

        /**
         * Get unformatted comment text
         *
         * @returns {string} Unformatted/stripped content text (plain text)
         */
        get: function () {
            return this.content.text();
        },

        /**
         * Get selected text.
         *
         * Note that this method ignores newlines
         *   @returns {string} Selected string or empty string if it fails.
         */
        getSelected: function () {
            if (this.hasFocus && window.getSelection)
                return window.getSelection().toString();
            else if (this._selectionRange)
                return this._selectionRange.toString();

            return '';
        },

        /**
         * Get index of cursor in text returned by .get.
         *
         * Ex. If the text box contains
         *     The[cursor] quick brown fox jumps over...
         * Then .offset() will return 3.
         *
         * @returns {number} The current cursor offset
         */
        offset: function () {
            return this.content.offset();
        },

        /**
         * Set unformatted comment text
         *
         * @param {string} str The plain text to be set as the content
         */
        set: function (str) {
            this.content.setText(str);
            this.resize();
            this.updatePlaceholderDisplay();
        },

        insertAtCursor: function (str) {
            // See http://phabricator.local.disqus.net/D18445#314506
            //
            // If the contentEditable div does not have focus when
            // insertNode is called (below), then insertNode could
            // insert the node anywhere on the page, wherever the user
            // has selected, i.e., whatever window.getSelection returns
            this.focus();

            // Include whitespace around string to separate from other text
            var text = ' ' + str + ' ';

            // Attempt to use insertText, which will insert the text
            // in a similar way to how the user would type it in.
            if (doc.queryCommandSupported && doc.queryCommandSupported('insertText')) {
                if (doc.execCommand('insertText', false, text))
                    return;
            }

            // Fallback if unable to use execCommand
            this.content.insertNode(doc.createTextNode(text));
        },

        clear: function () {
            parentProto.clear.call(this);
            _.defer(function (self) {
                self.$input.blur();  // mostly necessary for Chrome which keeps focus
            }, this);
        },

        insertAroundSelection: function (beforeText, afterText) {
            // See http://phabricator.local.disqus.net/D18445#314506
            //
            // If the contentEditable div does not have focus when
            // insertNode is called (below), then insertNode could
            // insert the node anywhere on the page, wherever the user
            // has selected, i.e., whatever window.getSelection returns
            this.focus();

            var selection = window.getSelection();
            // if rangeCount is 0, there is no selection to wrap
            if (!selection.rangeCount)
                return;

            var range = selection.getRangeAt(0);

            // Insert tags
            var afterRange = range.cloneRange();
            afterRange.collapse(false);
            var afterNode = doc.createTextNode(afterText);
            afterRange.insertNode(afterNode);

            var beforeRange = range.cloneRange();
            beforeRange.collapse(true);
            var beforeNode = doc.createTextNode(beforeText);
            beforeRange.insertNode(beforeNode);

            // Select original text again
            // NOTE: Does not maintain selection direction
            range.setStart(beforeNode, beforeText.length);
            range.setEnd(afterNode, 0);
            selection.removeAllRanges();
            selection.addRange(range);
        },
    });

    return ContentEditableView;
});

